import { ApiSettings, ApiSmsTemplate, ApiWhatsappTemplate } from "./interface.api";
import { Settings, SmsTemplate, WhatsappTemplate } from "@/types";
import { smsTemplatesMapper, whatsappTemplatesMapper} from "./mappers";
import { useCallback, useState } from "react";

import { ApiHooksProps } from "../interface";
import { SettingsApi } from "./interface";
import { request } from "../request";

const hook = ({ setError, setLoading, setSuccess, serverPath, token }: ApiHooksProps): SettingsApi => {
  const [settings, setSettings] = useState<Settings | null>(null)
  const [smsTemplates, setSmsTemplates] = useState<SmsTemplate[]>([])
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsappTemplate[]>([])
  
  const addTemplate = useCallback(async (data : SmsTemplate) => {
    const path =  `${serverPath}/settings/addTemplate/`;
    const body = {
      MessageText: data.text,
      Title: data.subject
    }

    await request(path, { body, method: 'POST', token });
  }, [serverPath, token]);
  
  const addWhatsappTemplate = useCallback(async (data : WhatsappTemplate) => {
    const path =  `${serverPath}/settings/addWhatsappTemplate/`;
    const body = {
      MessageText: data.text,
      Title: data.subject
    }

    await request(path, { body, method: 'POST', token });
  }, [serverPath, token]);

  const deleteTemplate = useCallback(async (id: string) => {
    const path = `${serverPath}/settings/deleteTemplate/${id}`;
    await request(path, { method: 'DELETE', token });
  }, [serverPath, token]);

  const deleteWhatsappTemplate = useCallback(async (id: string) => {
    const path = `${serverPath}/settings/deleteWhatsappTemplate/${id}`;
    await request(path, { method: 'DELETE', token });
  }, [serverPath, token]);

  const editTemplate = useCallback(async (data: SmsTemplate) => {
    const path = `${serverPath}/settings/editTemplate/${data.id}`;
    const body = {
      MessageText: data.text,
      Title: data.subject
    }

    await request(path, { body, method: 'POST', token });
  }, [serverPath, token]);

  const editWhatsappTemplate = useCallback(async (data: WhatsappTemplate) => {
    const path = `${serverPath}/settings/editWhatsappTemplate/${data.id}`;
    const body = {
      MessageText: data.text,
      Title: data.subject
    }

    await request(path, { body, method: 'POST', token });
  }, [serverPath, token]);

  const get = useCallback(async () => {
    const path = `${serverPath}/settings/get/`;
    const res = await request<never, ApiSettings>(path, { method: 'GET', token });
    setSettings({
      cancelFeeAmount: res.CancelFeeAmount,
      cancelFeeType: res.CancelFeeType,
      floorAreaSurchargeAmount: res.FloorAreaSurchargeAmount,
      floorAreaSurchargeThreshold: res.FloorAreaSurchargeThreshold,
      id: res.SettingId,
      invoiceDueDateDays: res.InvoiceDueDateDays,
      virtualTourImagesSurchargeAmount: res.VirtualTourImagesSurchargeAmount,
      virtualTourImagesThreshold: res.VirtualTourImagesThreshold
    })
  }, [serverPath, token]);

  const listTemplates = useCallback(async () => {
    const path = `${serverPath}/settings/listTemplates/`;
    const res = await request<never, ApiSmsTemplate[]>(path, { method: 'GET', token });

    setSmsTemplates(smsTemplatesMapper(res))
  }, [serverPath, token]);

  const listWhatsappTemplates = useCallback(async () => {
    const path = `${serverPath}/settings/listWhatsappTemplates/`;
    const res = await request<never, ApiWhatsappTemplate[]>(path, { method: 'GET', token });

    setWhatsappTemplates(whatsappTemplatesMapper(res))
  }, [serverPath, token]);

  const set = useCallback(async (data: Settings) => {
    const path = `${serverPath}/settings/set/`;
    const body: ApiSettings = {
      FloorAreaSurchargeAmount: data.floorAreaSurchargeAmount,
      FloorAreaSurchargeThreshold: data.floorAreaSurchargeThreshold,
      SettingId: data.id,
    }
    
    setLoading(true);
    try {
      await request<ApiSettings, Settings>(path, { body, token });
      setSettings(data);
      setSuccess('Settings updated!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token, settings]);

  const sendWhatsappMessage = useCallback(async (phoneNumber: string, message: string) => {
    const path = `${serverPath}/sms/whatsapp/send/`;
    const body = {
      Body: message,
      To: phoneNumber,
    }
    
    setLoading(true);
    try {
      await request<typeof body, never>(path, { body, token });
      setSuccess('Message sent!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token])

  const setVirtualTour = useCallback(async (data: Settings) => {
    const path = `${serverPath}/settings/set-virtual-tour/`;
    const body: ApiSettings = {
      SettingId: data.id,
      VirtualTourImagesSurchargeAmount: data.virtualTourImagesSurchargeAmount,
      VirtualTourImagesThreshold: data.virtualTourImagesThreshold,
    }
    
    setLoading(true);
    try {
      await request<ApiSettings, Settings>(path, { body, token });
      setSettings(data);
      setSuccess('Settings updated!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token, settings]);

  const setCancelFee = useCallback(async (data: Settings) => {
    const path = `${serverPath}/settings/set-cancel-fee/`;
    const body: ApiSettings = {
      CancelFeeAmount: data.cancelFeeAmount,
      CancelFeeType: data.cancelFeeType,
      SettingId: data.id,
    }
    
    setLoading(true);
    try {
      await request<ApiSettings, Settings>(path, { body, token });
      setSettings(data);
      setSuccess('Settings updated!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token, settings]);

  const setInvoiceDueDays = useCallback(async (data: Settings) => {
    const path = `${serverPath}/settings/set-invoice-due/`;
    const body: ApiSettings = {
      InvoiceDueDateDays: data.invoiceDueDateDays,
      SettingId: data.id,
    }
    
    setLoading(true);
    try {
      await request<ApiSettings, Settings>(path, { body, token });
      setSettings(data);
      setSuccess('Settings updated!');
    } catch (error: unknown) {
      if (typeof error === 'string') {
        setError(error);
      }
      else if (error instanceof Error) {
        setError(error.message || '');
      }
    }
    setLoading(false);
  }, [serverPath, token, settings]);

  return {
    addTemplate,
    addWhatsappTemplate,
    deleteTemplate,
    deleteWhatsappTemplate,
    editTemplate,
    editWhatsappTemplate,
    get,
    listTemplates,
    listWhatsappTemplates,
    sendWhatsappMessage,
    set,
    setCancelFee,
    setInvoiceDueDays,
    setVirtualTour,
    settings,
    smsTemplates,
    whatsappTemplates
  }
}

export default hook;